import { Box, Button, Card, IconButton, Stack, Typography } from '@dtx-company/design-system/src'
import { CardActions } from '@mui/material'
import { Close, EmojiObjectsOutlined, LockOutlined, OpenInNewOutlined } from '@mui/icons-material'
import { FC, useRef } from 'react'
import { selectSection } from '../../../machines/locg/locgSelectors'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useLOCGPrismicContext } from '../hooks/useLOCGPrismicContext'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import { useSelector } from '@xstate/react'

interface BestPracticesProps {
  titleId?: string
  descriptionId?: string
  handleClose?: () => void
}

export const BestPractices: FC<BestPracticesProps> = ({ titleId, descriptionId, handleClose }) => {
  const { isTabletOrDesktop } = useScreenSizeBreakpoints()
  const { locgService } = useGlobalServices()
  const activeSectionId = useSelector(locgService, selectSection)
  const {
    locgV2Data: { best_practices }
  } = useLOCGPrismicContext()
  const bestPractice = best_practices?.find(content => content.key === activeSectionId)
  const showCta = bestPractice?.ispro || bestPractice?.link
  const CtaIcon = bestPractice?.ispro ? LockOutlined : OpenInNewOutlined
  const mobileRef = useRef<HTMLButtonElement>(null)
  const desktopRef = useRef<HTMLButtonElement>(null)
  if (!bestPractice) return null

  const analyticsDataAttributes = {
    'data-testid': 'best-practices-link',
    'data-heap-id': 'LoggedOutGenerator_BestPracticesLink',
    'data-section': activeSectionId,
    'data-is-pro-link': bestPractice.ispro
  }

  const ctaProps = {
    href: bestPractice.link as string,
    target: '_blank',
    rel: 'noreferrer'
  }
  // Proxy clicks on the card to the cta button within the card on tablet and desktop
  const proxyCardClick = (): void => {
    if (showCta) {
      isTabletOrDesktop ? desktopRef.current?.click() : mobileRef.current?.click()
    }
  }

  return (
    <Card
      variant="outlined"
      sx={theme => ({
        position: 'relative',
        p: 4,
        ...(showCta && {
          cursor: {
            md: 'pointer'
          },
          '&:hover': {
            boxShadow: {
              md: 1
            }
          }
        }),
        '&.MuiCard-root': {
          minHeight: { xs: theme.spacing(35), sm: 'auto' },
          height: {
            xs: 'auto',
            sm: theme.spacing(37),
            lg: theme.spacing(28),
            xl: theme.spacing(26)
          }
        }
      })}
      {...(isTabletOrDesktop && {
        ...analyticsDataAttributes,
        onClick: proxyCardClick
      })}
    >
      <Stack gap={2}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            ...(showCta && { pr: 9 })
          }}
        >
          <EmojiObjectsOutlined sx={{ display: { xs: 'none', md: 'block' }, mr: 2 }} />
          <Typography
            variant={{ xs: 'subtitleMedium', md: 'labelLarge' }}
            component="h2"
            id={titleId}
            data-testid={`best-practices-${activeSectionId}-title`}
          >
            {bestPractice?.title}
          </Typography>
        </Stack>

        <Typography
          variant="bodySmall"
          component="span"
          id={descriptionId}
          data-testid={`best-practices-${activeSectionId}-description`}
          color={{ xs: 'onSurface.secondary', md: 'onSurface.primary' }}
        >
          {bestPractice?.description}
        </Typography>

        {/* Mobile */}
        {!isTabletOrDesktop && (
          <CardActions
            sx={theme => ({
              display: {
                xs: 'flex',
                md: 'none'
              },
              justifyContent: 'flex-end',
              gap: 2,
              p: 0,
              marginRight: theme.spacing(-2),
              marginBottom: theme.spacing(-2)
            })}
          >
            <Box
              sx={theme => ({
                position: 'absolute',
                top: theme.spacing(2),
                right: theme.spacing(2)
              })}
              data-testid={`best-practices-${activeSectionId}-mobile-close`}
            >
              <IconButton
                icon={Close}
                size="small"
                variant="text"
                aria-label="Close"
                onClick={handleClose}
              />
            </Box>
            {bestPractice?.mobile_cta && (
              <Button
                variant="text"
                ref={mobileRef}
                {...ctaProps}
                {...analyticsDataAttributes}
                data-testid={`best-practices-${activeSectionId}-mobile-cta`}
              >
                {bestPractice?.mobile_cta}
              </Button>
            )}
          </CardActions>
        )}

        {/* Desktop */}
        {showCta && (
          <Box
            sx={theme => ({
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              top: theme.spacing(2),
              right: theme.spacing(2)
            })}
            data-testid={`best-practices-${activeSectionId}-desktop-cta`}
          >
            <IconButton
              icon={CtaIcon}
              size="small"
              variant="text"
              aria-label={bestPractice?.mobile_cta}
              ref={desktopRef}
              {...ctaProps}
            />
          </Box>
        )}
      </Stack>
    </Card>
  )
}
